/* REST API client */

import isNode from 'detect-node';
import rest from 'rest';
import defaultRequest from 'rest/interceptor/defaultRequest';
import errorCode from 'rest/interceptor/errorCode';
import mime from 'rest/interceptor/mime';
import pathPrefix from 'rest/interceptor/pathPrefix';

import {getAuthenticationToken} from 'App/features/user/userApi';

import BaseRequest from '../baseRequest';
import getConfig from '../load_config';
import {saveOnClient} from '../utils/misc_utils';

import * as filters from './rest_params_formatting';

let restOrigin;
let baseClient;
let restClient;
const modelClients = {}; // map of models' sub-clients

export function initRestClient(config) {
  if (!isNode) {
    restOrigin = config.apiOrigin;
  }

  baseClient = rest
    .wrap(mime, {mime: 'application/json'})
    .wrap(errorCode, {code: 400})
    .wrap(pathPrefix, {prefix: restOrigin});
  restClient = baseClient; // unauthenticated by default
}

function removeOrganization(entity) {
  // to work around a back-end API limitation
  let cleanEntity;
  if (typeof entity.organization !== 'undefined' && !entity.organization) {
    cleanEntity = {
      ...entity,
    };
    delete cleanEntity.organization;
  } else {
    cleanEntity = entity;
  }
  return cleanEntity;
}

class ModelClient {
  // generates one sub-client per model
  constructor(modelName) {
    if (!restClient) {
      const config = getConfig();
      initRestClient(config);
    }

    this.modelName = modelName;
    this.subClient = null;
    this.baseRequest = null;
    this.setSubClient();
  }

  setSubClient() {
    this.subClient = restClient.wrap(pathPrefix, {
      headers: {Authorization: getAuthenticationToken()},
      prefix: `/${this.modelName}/`,
    });
    this.baseRequest = new BaseRequest(this.modelName);
  }

  downloableQuery(params) {
    const queryParams = Object.keys(params)
      .map(k => encodeURIComponent(k) + '=' + encodeURIComponent(params[k]))
      .join('&');

    return fetch(`${restOrigin}/${this.modelName}/?${queryParams}`, {
      method: 'get',
      headers: {
        Accept: params.mediaType,
        'Content-Type': 'application/json',
        Authorization: getAuthenticationToken(),
      },
    })
      .then(response => {
        if (!response.ok) {
          console.error('Request failed');
        }
        return response.blob();
      })
      .then(responseBlob => {
        const datetime = new Date();
        const filename = `buildrz-${datetime.toLocaleDateString(
          'fr-FR',
        )}-${datetime.toLocaleTimeString('fr-FR')}.xlsx`;
        saveOnClient(responseBlob, filename);
      })
      .catch(error => {
        console.error('Request failed', error);
      });
  }

  query(params, nopagination = false) {
    const restParams = filters.mapQueryParamsToRestParams(params);
    if (params.downloadable) {
      return this.downloableQuery(restParams);
    }

    return this.baseRequest.request('GET', {
      queryParameters: restParams,
      callback: response => (nopagination ? response : response.results),
    });
  }

  fetch(id, {params = {}} = {}) {
    return this.baseRequest.get(id.toString(), {
      queryParameters: params,
    });
  }

  update(entity) {
    const cleanEntity = removeOrganization(entity);
    const id = cleanEntity.hash || cleanEntity.id.toString();
    return this.subClient({
      headers: {Authorization: getAuthenticationToken()},
      method: 'PUT',
      path: `${id}/`,
      entity: cleanEntity,
    }).entity();
  }

  create(entity) {
    const cleanEntity = removeOrganization(entity);
    return this.subClient({
      headers: {Authorization: getAuthenticationToken()},
      method: 'POST',
      entity: cleanEntity,
    }).entity();
  }

  delete(entity) {
    const id = entity.hash ? entity.hash : entity.id.toString();
    return this.subClient({
      headers: {Authorization: getAuthenticationToken()},
      method: 'DELETE',
      path: `${id}/`,
    }).entity();
  }

  reset() {
    // on logout
    this.subClient = null;
  }
}

export function getClient() {
  return baseClient.wrap(defaultRequest, {
    headers: {Authorization: getAuthenticationToken()},
  });
}

export function model(modelName) {
  if (!modelClients[modelName]) {
    modelClients[modelName] = new ModelClient(modelName);
  }
  return modelClients[modelName];
}

export function reset() {
  // on logout
  Object.keys(modelClients).forEach(oneModelName => {
    modelClients[oneModelName].reset();
    delete modelClients[oneModelName];
  });
  restClient = baseClient;
}

export function login(email, password) {
  return restClient({
    headers: {Authorization: getAuthenticationToken()},
    path: '/login/',
    method: 'POST',
    entity: {
      email,
      password,
    },
  });
}

export function signup(name, email, password1, password2) {
  return restClient({
    headers: {Authorization: getAuthenticationToken()},
    path: '/register/',
    method: 'POST',
    entity: {
      name,
      email,
      password1,
      password2,
    },
  });
}

export function updateUser(user) {
  const cleanUser = removeOrganization(user);
  return restClient({
    headers: {Authorization: getAuthenticationToken()},
    path: '/me/',
    method: 'POST',
    entity: cleanUser,
  }).entity();
}

export function authenticate(token) {
  const client = baseClient.wrap(defaultRequest, {
    headers: {Authorization: token},
  });
  return client({
    method: 'GET',
    path: '/me/',
  });
}

export function resetPassword(email) {
  return restClient({
    headers: {Authorization: getAuthenticationToken()},
    path: '/auth/password-reset/',
    method: 'POST',
    entity: {
      email,
    },
  });
}

export function shareItem(entity) {
  return restClient({
    headers: {Authorization: getAuthenticationToken()},
    path: '/share/',
    method: 'POST',
    entity,
  });
}

export function getZoneInfo(geom) {
  return restClient({
    headers: {Authorization: getAuthenticationToken()},
    path: '/zone-info/',
    method: 'POST',
    entity: {geom},
  }).entity();
}
